/**
 * Pure geometry for the admin print-area editor.
 * All values are in the mockup's native pixel space.
 */

export interface AreaRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface AreaBounds {
  mockupWidth: number;
  mockupHeight: number;
}

export type DragMode =
  | 'move'
  | 'nw'
  | 'n'
  | 'ne'
  | 'e'
  | 'se'
  | 's'
  | 'sw'
  | 'w';

export const MIN_AREA_SIZE_PX = 10;

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const roundRect = (rect: AreaRect): AreaRect => ({
  x: Math.round(rect.x),
  y: Math.round(rect.y),
  width: Math.round(rect.width),
  height: Math.round(rect.height),
});

/** Move the area by a delta, clamped to the mockup bounds. */
export function moveArea(
  area: AreaRect,
  dx: number,
  dy: number,
  bounds: AreaBounds
): AreaRect {
  return roundRect({
    ...area,
    x: clamp(area.x + dx, 0, bounds.mockupWidth - area.width),
    y: clamp(area.y + dy, 0, bounds.mockupHeight - area.height),
  });
}

/** Resize the area from its top-left anchor, clamped to bounds and min size. */
export function resizeArea(
  area: AreaRect,
  dw: number,
  dh: number,
  bounds: AreaBounds
): AreaRect {
  return roundRect({
    ...area,
    width: clamp(
      area.width + dw,
      MIN_AREA_SIZE_PX,
      bounds.mockupWidth - area.x
    ),
    height: clamp(
      area.height + dh,
      MIN_AREA_SIZE_PX,
      bounds.mockupHeight - area.y
    ),
  });
}

/**
 * Apply a pointer drag (move or handle resize) to a starting rect.
 * dx/dy are pointer deltas already converted to native mockup pixels.
 * With `fromCenter` the centre stays put and the opposite edges mirror the
 * dragged ones; `proportional` then holds for side handles too.
 */
export function applyDragToArea(
  mode: DragMode,
  start: AreaRect,
  dx: number,
  dy: number,
  bounds: AreaBounds,
  proportional = false,
  fromCenter = false
): AreaRect {
  if (mode === 'move') {
    return moveArea(start, dx, dy, bounds);
  }

  if (fromCenter) {
    return applyCenteredResize(mode, start, dx, dy, bounds, proportional);
  }

  if (proportional && isCornerMode(mode)) {
    return applyProportionalCornerResize(mode, start, dx, dy, bounds);
  }

  return applyResizeDrag(mode, start, dx, dy, bounds);
}

/** Corner handles carry both a horizontal and a vertical letter. */
function isCornerMode(mode: DragMode): boolean {
  return mode.length === 2;
}

/**
 * Resize from a corner while keeping the start aspect ratio. The opposite
 * corner stays fixed; the dominant pointer axis drives the scale, and the
 * result is shrunk to keep the whole rect inside the bounds (aspect intact).
 */
function applyProportionalCornerResize(
  mode: DragMode,
  start: AreaRect,
  dx: number,
  dy: number,
  bounds: AreaBounds
): AreaRect {
  // A degenerate start (zero/negative width or height) makes the aspect ratio
  // and scale factor non-finite, which would propagate NaN through every guard
  // below. Fall back to a plain clamped resize so the rect stays valid.
  if (!(start.width > 0) || !(start.height > 0)) {
    return applyResizeDrag(mode, start, dx, dy, bounds);
  }

  const aspect = start.width / start.height;
  const right = start.x + start.width;
  const bottom = start.y + start.height;
  const anchorRight = mode.includes('w'); // dragging a west handle fixes the right edge
  const anchorBottom = mode.includes('n'); // dragging a north handle fixes the bottom edge

  // Free-resize proposals per axis (positive = grow).
  const dWidth = mode.includes('e') ? dx : -dx;
  const dHeight = mode.includes('s') ? dy : -dy;

  // Follow whichever axis the pointer moved more (relative to the start size).
  const scaleW = (start.width + dWidth) / start.width;
  const scaleH = (start.height + dHeight) / start.height;
  const factor = Math.abs(scaleW - 1) >= Math.abs(scaleH - 1) ? scaleW : scaleH;

  let width = Math.max(MIN_AREA_SIZE_PX, start.width * factor);
  let height = width / aspect;
  if (height < MIN_AREA_SIZE_PX) {
    height = MIN_AREA_SIZE_PX;
    width = height * aspect;
  }

  // Clamp to the space available from the fixed corner, keeping the ratio.
  const maxWidth = anchorRight ? right : bounds.mockupWidth - start.x;
  const maxHeight = anchorBottom ? bottom : bounds.mockupHeight - start.y;
  if (width > maxWidth) {
    width = maxWidth;
    height = width / aspect;
  }
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspect;
  }

  const x = anchorRight ? right - width : start.x;
  const y = anchorBottom ? bottom - height : start.y;
  return roundRect({ x, y, width, height });
}

/**
 * Resize around the start rect's centre: every dragged edge pulls its
 * opposite edge along by the same amount. Proportional, the axis the
 * pointer moved more (relative to the size) scales both; otherwise only the
 * dragged axes change, so the right handle moves left and right together.
 * The rect stays inside the bounds without leaving its centre.
 */
function applyCenteredResize(
  mode: DragMode,
  start: AreaRect,
  dx: number,
  dy: number,
  bounds: AreaBounds,
  proportional: boolean
): AreaRect {
  const centreX = start.x + start.width / 2;
  const centreY = start.y + start.height / 2;
  const maxWidth = 2 * Math.min(centreX, bounds.mockupWidth - centreX);
  const maxHeight = 2 * Math.min(centreY, bounds.mockupHeight - centreY);

  // Growth per axis: the pointer moves one edge, the mirror edge doubles it.
  const horizontal = mode.includes('e') || mode.includes('w');
  const vertical = mode.includes('n') || mode.includes('s');
  const dWidth = horizontal ? 2 * (mode.includes('e') ? dx : -dx) : 0;
  const dHeight = vertical ? 2 * (mode.includes('s') ? dy : -dy) : 0;

  let width: number;
  let height: number;
  if (proportional && start.width > 0 && start.height > 0) {
    const scaleW = (start.width + dWidth) / start.width;
    const scaleH = (start.height + dHeight) / start.height;
    let factor =
      horizontal && (!vertical || Math.abs(scaleW - 1) >= Math.abs(scaleH - 1))
        ? scaleW
        : scaleH;
    factor = Math.max(
      factor,
      MIN_AREA_SIZE_PX / start.width,
      MIN_AREA_SIZE_PX / start.height
    );
    factor = Math.min(factor, maxWidth / start.width, maxHeight / start.height);
    width = start.width * factor;
    height = start.height * factor;
  } else {
    width = clamp(start.width + dWidth, MIN_AREA_SIZE_PX, maxWidth);
    height = clamp(start.height + dHeight, MIN_AREA_SIZE_PX, maxHeight);
  }

  return roundRect({
    x: centreX - width / 2,
    y: centreY - height / 2,
    width,
    height,
  });
}

function applyResizeDrag(
  mode: DragMode,
  start: AreaRect,
  dx: number,
  dy: number,
  bounds: AreaBounds
): AreaRect {
  let { x, y, width, height } = start;
  const right = start.x + start.width;
  const bottom = start.y + start.height;

  if (mode.includes('w')) {
    x = clamp(start.x + dx, 0, right - MIN_AREA_SIZE_PX);
    width = right - x;
  }
  if (mode.includes('e')) {
    width = clamp(
      start.width + dx,
      MIN_AREA_SIZE_PX,
      bounds.mockupWidth - start.x
    );
  }
  if (mode.includes('n')) {
    y = clamp(start.y + dy, 0, bottom - MIN_AREA_SIZE_PX);
    height = bottom - y;
  }
  if (mode.includes('s')) {
    height = clamp(
      start.height + dy,
      MIN_AREA_SIZE_PX,
      bounds.mockupHeight - start.y
    );
  }

  return roundRect({ x, y, width, height });
}

/** Centre the area on one or both axes, keeping its size, clamped to bounds. */
export function centerArea(
  area: AreaRect,
  bounds: AreaBounds,
  axis: 'x' | 'y' | 'both'
): AreaRect {
  let { x, y } = area;
  if (axis === 'x' || axis === 'both') {
    x = clamp(
      bounds.mockupWidth / 2 - area.width / 2,
      0,
      bounds.mockupWidth - area.width
    );
  }
  if (axis === 'y' || axis === 'both') {
    y = clamp(
      bounds.mockupHeight / 2 - area.height / 2,
      0,
      bounds.mockupHeight - area.height
    );
  }
  return roundRect({ ...area, x, y });
}

/**
 * Resize the area to an absolute width/height while keeping its centre fixed,
 * clamped to the min size and the mockup bounds. Used by the scale control so
 * a frame grows or shrinks in place rather than from its top-left corner.
 */
export function resizeAboutCenter(
  area: AreaRect,
  width: number,
  height: number,
  bounds: AreaBounds
): AreaRect {
  const w = clamp(Math.round(width), MIN_AREA_SIZE_PX, bounds.mockupWidth);
  const h = clamp(Math.round(height), MIN_AREA_SIZE_PX, bounds.mockupHeight);
  const centreX = area.x + area.width / 2;
  const centreY = area.y + area.height / 2;
  return roundRect({
    x: clamp(centreX - w / 2, 0, bounds.mockupWidth - w),
    y: clamp(centreY - h / 2, 0, bounds.mockupHeight - h),
    width: w,
    height: h,
  });
}

/** Default snap radius in SCREEN pixels; the editor divides by the zoom
 * scale to get the native-pixel threshold so the magnet feels the same at
 * every zoom level. */
export const SNAP_SCREEN_PX = 8;

export interface SnapResult {
  rect: AreaRect;
  /** The area centre locked onto the mockup's vertical centre line. */
  snappedX: boolean;
  /** The area centre locked onto the mockup's horizontal centre line. */
  snappedY: boolean;
}

/**
 * Magnetically snap the area's centre to the mockup's centre lines.
 * When the centre is within `thresholdPx` (native px) of the vertical or
 * horizontal mid-line, the area is nudged so it sits exactly centred on
 * that axis. Each axis snaps independently; the result stays in bounds.
 */
export function snapAreaToCenter(
  area: AreaRect,
  bounds: AreaBounds,
  thresholdPx: number
): SnapResult {
  const centreX = bounds.mockupWidth / 2;
  const centreY = bounds.mockupHeight / 2;
  const areaCentreX = area.x + area.width / 2;
  const areaCentreY = area.y + area.height / 2;

  let { x, y } = area;
  let snappedX = false;
  let snappedY = false;

  if (Math.abs(areaCentreX - centreX) <= thresholdPx) {
    x = centreX - area.width / 2;
    snappedX = true;
  }
  if (Math.abs(areaCentreY - centreY) <= thresholdPx) {
    y = centreY - area.height / 2;
    snappedY = true;
  }

  return {
    rect: roundRect({
      ...area,
      x: clamp(x, 0, bounds.mockupWidth - area.width),
      y: clamp(y, 0, bounds.mockupHeight - area.height),
    }),
    snappedX,
    snappedY,
  };
}
